import type { ReportPerson, SalesReportResponse, WorkBlockStateView, WorkBlockView } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { Icon } from "../design/components/Icon";
import { PlainButton } from "../design/components/PlainButton";
import { Reason } from "../design/components/Reason";
import { Select } from "../design/components/Select";
import { messageForError } from "../i18n/errors";
import { formatCount, formatNumericTime } from "../i18n/format";
import type { Locale } from "@gioia365/i18n";
import { useLocale, useT, type Translate } from "../i18n/locale";
import { dayLongText, formatKpi, sentenceStart } from "./report-format";
import { useSwitchWorkBlocks, workBlocksQuery } from "./report-queries";
import { WorkBlocksSwitchDialog } from "./WorkBlocksSwitchDialog";

// «La giornata a blocchi di 30 minuti» (piano M7, Task 7; D10; mock-up `blocksSection` del 17/10/2026, scelta 11 della nota; SEZ-4,
// SEZ-16; domanda n. 34, aperta), in fondo a «Commerciali». Spenta (lo dice «Commerciali», `workBlocks.enabled`), la sezione ha soltanto
// il lucchetto e il perché, nessuna barra, e non legge la giornata; nel contesto di piattaforma, fuori dalla «Guarda come…», il Super
// admin di Gioia365 la accende per l'azienda con «Accendi per {azienda}», dopo una conferma (`WorkBlocksSwitchDialog.tsx`), e la spegne
// con «Spegni per {azienda}», senza. Accesa, legge la giornata di una persona in un giorno (`GET /api/reports/work-blocks`): «Persona»
// soltanto per chi vede il team e ha più persone fra cui scegliere (le scelte di «Di chi», o tutte), «Giorno» con i giorni lavorativi del
// periodo dal più recente; persona e giorno restano nello stato della pagina (D11), di partenza la prima persona e il giorno più recente.
// Dopo un interruttore riuscito il focus va sul titolo della sezione, che resta al suo posto mentre la sezione cambia stato (il pulsante
// che l'aveva non c'è più). Accesa, in un periodo senza giorni lavorativi (o senza persone) la sezione non c'è, come nel mock-up; per
// Gioia365 resta il titolo con «Spegni per {azienda}», così si spegne anche da lì.
// Sotto, i fatti del giorno («Al telefono», «Solo al computer», «Fermo», «Chiamate»), una barra per mezz'ora (sopra le chiamate, in blu
// quelle con risposta; sotto, in «+N», le altre attività) con il dettaglio nella riga `role="status"` al passaggio, al focus e al tocco, e
// la legenda. Lo stato si legge anche senza colore: il bordo sotto ogni barra ha un tratto diverso, pausa, chiuso e non ancora sono a
// righe, e ogni barra ha la sua frase. Sotto 720 px, o con il dito, ogni barra è larga almeno 44 px e le barre scorrono di lato nel loro
// riquadro (UX_SYSTEM.md, ruling del registro del Task 4), mai la pagina.

/** Gli stati che sono lavoro: gli altri (pausa, chiuso, non ancora) sono a righe e la loro frase non ha numeri, se non ce ne sono. */
const WORKING: ReadonlySet<WorkBlockStateView> = new Set(["phone", "screen", "idle"]);

/** Quante mezz'ore fra due etichette delle ore sotto le barre: ogni due ore, come il mock-up. */
const TICK_EVERY = 4;

/** «13» per le 13:00, «12:30» per le 12:30: le ore della legenda («pausa 13–14»). */
function shortClock(timeZone: string, iso: string): string {
  const [hour = "0", minute = "00"] = formatNumericTime(timeZone, iso).split(":");
  return minute === "00" ? String(Number(hour)) : `${Number(hour)}:${minute}`;
}

/** I minuti di un numero di secondi, arrotondati una volta, come «Minuti al telefono» («5 min», «1 h 2 min»). */
const minutesOf = (locale: Locale, seconds: number) => formatKpi(locale, "minutes", Math.round(seconds / 60));

/** La frase di una mezz'ora (`text` del mock-up): l'ora, lo stato e, per il lavoro o se c'è qualcosa, i numeri. */
function blockSentence(t: Translate, locale: Locale, timeZone: string, block: WorkBlockView): string {
  const span = t("reports.blocks.span", {
    from: formatNumericTime(timeZone, block.start),
    to: formatNumericTime(timeZone, block.end),
    state: t(`reports.blocks.states.${block.state}`),
  });
  if (!WORKING.has(block.state) && block.calls === 0 && block.activities === 0) return span;
  return t("reports.blocks.numbers", {
    span,
    calls: block.calls === 1 ? t("reports.blocks.callsOne") : t("reports.blocks.callsMany", { count: formatCount(locale, block.calls) }),
    answered: formatCount(locale, block.answered),
    time: minutesOf(locale, block.callSeconds),
    activities: block.activities === 1 ? t("reports.blocks.activitiesOne") : t("reports.blocks.activitiesMany", { count: formatCount(locale, block.activities) }),
  });
}

type Switchable = { company: string } | null;

type Props = {
  report: SalesReportResponse;
  timeZone: string;
  /** La persona e il giorno scelti, nello stato della pagina; `null` finché non si sceglie. */
  person: string | null;
  day: string | null;
  onPersonChange: (membershipId: string) => void;
  onDayChange: (day: string) => void;
  /** L'azienda, quando chi guarda è il Super admin di Gioia365 nel contesto di piattaforma e può accenderla e spegnerla. */
  switchable: Switchable;
};

/** Il titolo della sezione: si può dargli il focus da codice (dopo un interruttore), non con il Tab. */
function SectionTitle({ id }: { id: string }) {
  const t = useT();
  return (
    <h2 id={id} tabIndex={-1}>
      {t("reports.blocks.title")}
    </h2>
  );
}

export function WorkBlocksSection(props: Props) {
  const titleId = useId();
  const enabled = props.report.workBlocks.enabled;
  // Dopo un interruttore riuscito: lo stato atteso. Il focus va sul titolo quando la sezione lo mostra (dopo la rilettura di
  // «Commerciali», che può arrivare prima o dopo la fine del salvataggio); `focusRequest` fa ricontrollare se è già arrivata.
  const focusWhen = useRef<boolean | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  useEffect(() => {
    if (focusWhen.current === null || focusWhen.current !== enabled) return;
    focusWhen.current = null;
    document.getElementById(titleId)?.focus();
  }, [focusRequest, enabled, titleId]);
  const onSwitched = useCallback((next: boolean) => {
    focusWhen.current = next;
    setFocusRequest((count) => count + 1);
  }, []);
  if (!enabled) {
    return (
      <section className="g-report-section g-report-panel" aria-labelledby={titleId}>
        <div className="g-report-head">
          <SectionTitle id={titleId} />
        </div>
        <OffNotice version={props.report.workBlocks.version} switchable={props.switchable} onSwitched={onSwitched} />
      </section>
    );
  }
  return <WorkBlocksDay {...props} titleId={titleId} onSwitched={onSwitched} />;
}

/** La sezione spenta: il lucchetto, il perché e, per Gioia365, «Accendi per {azienda}» con la conferma. */
function OffNotice({ version, switchable, onSwitched }: { version: number; switchable: Switchable; onSwitched: (enabled: boolean) => void }) {
  const t = useT();
  const tab = useTab();
  const change = useSwitchWorkBlocks(tab);
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function turnOn() {
    if (change.isPending) return;
    setFailure(null);
    try {
      await change.mutateAsync({ enabled: true, expectedVersion: version });
      setConfirming(false);
      onSwitched(true);
    } catch (refusal) {
      setFailure(messageForError(t, refusal));
    }
  }

  return (
    <>
      <div className="g-report-blocks-off">
        <Icon name="lock" size="md" />
        <p>{t("reports.blocks.off")}</p>
      </div>
      {switchable ? (
        <div className="g-report-blocks-switch">
          <Button
            onClick={() => {
              setFailure(null);
              setConfirming(true);
            }}
          >
            {t("reports.blocks.turnOn", { company: switchable.company })}
          </Button>
        </div>
      ) : null}
      {confirming && switchable ? (
        <WorkBlocksSwitchDialog company={switchable.company} pending={change.isPending} failure={failure} onConfirm={() => void turnOn()} onClose={() => setConfirming(false)} />
      ) : null}
    </>
  );
}

/**
 * «Spegni per {azienda}»: senza conferma; un rifiuto si dice accanto al pulsante. La promessa e non le funzioni di `mutate`: la sezione
 * può smontare questo pulsante (la rilettura dice già «spenta») prima che il salvataggio finisca, e allora quelle non partirebbero.
 */
function TurnOff({ version, company, onSwitched }: { version: number; company: string; onSwitched: (enabled: boolean) => void }) {
  const t = useT();
  const tab = useTab();
  const change = useSwitchWorkBlocks(tab);
  const [failure, setFailure] = useState<string | null>(null);
  return (
    <div className="g-report-blocks-switch">
      {failure ? (
        <Reason tone="warning" role="alert">
          {failure}
        </Reason>
      ) : null}
      <Button
        disabled={change.isPending}
        onClick={() => {
          setFailure(null);
          change.mutateAsync({ enabled: false, expectedVersion: version }).then(
            () => onSwitched(false),
            (refusal: unknown) => setFailure(messageForError(t, refusal)),
          );
        }}
      >
        {t("reports.blocks.turnOff", { company })}
      </Button>
    </div>
  );
}

/** Le persone fra cui scegliere: con `own` la persona; per chi vede il team le scelte di «Di chi», o tutte. */
function choicesOf(report: SalesReportResponse): ReportPerson[] {
  if (report.scope === "own" || report.chosen.length === 0) return report.people;
  const chosen = new Set(report.chosen);
  return report.people.filter((person) => chosen.has(person.membershipId));
}

function WorkBlocksDay({
  report,
  timeZone,
  person,
  day,
  onPersonChange,
  onDayChange,
  switchable,
  titleId,
  onSwitched,
}: Props & { titleId: string; onSwitched: (enabled: boolean) => void }) {
  const t = useT();
  const tab = useTab();
  const ids = useId();
  // La mezz'ora scelta, non la sua frase: se la giornata si rilegge, il dettaglio dice quella di adesso.
  const [chosen, setChosen] = useState<number | null>(null);
  const people = choicesOf(report);
  // I giorni lavorativi del periodo, dal più recente (`days.slice().reverse()` del mock-up).
  const days = report.days
    .filter((candidate) => candidate.working)
    .map((candidate) => candidate.day)
    .reverse();
  const personId = people.some((candidate) => candidate.membershipId === person) ? person : (people[0]?.membershipId ?? null);
  const dayId = day !== null && days.includes(day) ? day : (days[0] ?? null);
  const blocksRead = useQuery({ ...workBlocksQuery(tab, personId ?? "", dayId ?? ""), enabled: personId !== null && dayId !== null });
  if (personId === null || dayId === null) {
    if (!switchable) return null;
    return (
      <section className="g-report-section g-report-panel" aria-labelledby={titleId}>
        <div className="g-report-head">
          <div>
            <SectionTitle id={titleId} />
            {dayId === null ? <p className="g-report-hint">{t("reports.blocks.noWorkingDay")}</p> : null}
          </div>
        </div>
        <TurnOff version={report.workBlocks.version} company={switchable.company} onSwitched={onSwitched} />
      </section>
    );
  }
  const data = blocksRead.data;
  if (data !== undefined && !data.enabled) {
    return (
      <section className="g-report-section g-report-panel" aria-labelledby={titleId}>
        <div className="g-report-head">
          <SectionTitle id={titleId} />
        </div>
        <OffNotice version={report.workBlocks.version} switchable={switchable} onSwitched={onSwitched} />
      </section>
    );
  }

  const name = data?.person.displayName ?? people.find((candidate) => candidate.membershipId === personId)?.displayName ?? "";
  const dayText = dayLongText(t, dayId);
  const until = dayId === report.range.to && report.range.endsAt !== null ? t("reports.blocks.dayUntil", { day: dayText, time: formatNumericTime(timeZone, report.range.endsAt) }) : dayText;
  const selects = (
    <div className="g-report-blocks__selects">
      {report.scope === "all" && people.length > 1 ? (
        <Select
          id={`${ids}-person`}
          label={t("reports.blocks.person")}
          value={personId}
          options={people.map((candidate) => ({ value: candidate.membershipId, label: candidate.displayName }))}
          onChange={(value) => {
            setChosen(null);
            onPersonChange(value);
          }}
        />
      ) : null}
      {days.length > 1 ? (
        <Select
          id={`${ids}-day`}
          label={t("reports.blocks.day")}
          value={dayId}
          options={days.map((candidate) => ({ value: candidate, label: dayLongText(t, candidate) }))}
          onChange={(value) => {
            setChosen(null);
            onDayChange(value);
          }}
        />
      ) : null}
    </div>
  );

  let body;
  if (blocksRead.isError) {
    body = (
      <div className="g-report-blocks-error">
        <Reason tone="warning" role="alert">
          {t("reports.blocks.error")}
        </Reason>
        <Button onClick={() => void blocksRead.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else if (data === undefined) {
    body = (
      <p className="g-report-hint" aria-busy="true">
        {t("reports.blocks.loading")}
      </p>
    );
  } else {
    body = <BlocksOfDay blocks={data.blocks} timeZone={timeZone} chosen={chosen} onChoose={setChosen} />;
  }

  return (
    <section className="g-report-section g-report-panel" aria-labelledby={titleId}>
      <div className="g-report-head">
        <div>
          <SectionTitle id={titleId} />
          <p className="g-report-hint">{t("reports.blocks.intro", { name, day: until })}</p>
        </div>
        {selects}
      </div>
      {body}
      {switchable ? <TurnOff version={report.workBlocks.version} company={switchable.company} onSwitched={onSwitched} /> : null}
    </section>
  );
}

/** I fatti del giorno, le barre, le ore sotto, il dettaglio e la legenda. */
function BlocksOfDay({ blocks, timeZone, chosen, onChoose }: { blocks: readonly WorkBlockView[]; timeZone: string; chosen: number | null; onChoose: (index: number) => void }) {
  const t = useT();
  const { locale } = useLocale();
  const count = (state: WorkBlockStateView) => blocks.filter((block) => block.state === state).length;
  const blocksWord = (n: number) => (n === 1 ? t("reports.blocks.blocksOne") : t("reports.blocks.blocksMany", { count: formatCount(locale, n) }));
  const factOf = (state: "phone" | "screen" | "idle") => t("reports.blocks.factBlocks", { blocks: blocksWord(count(state)), time: formatKpi(locale, "minutes", count(state) * 30) });
  const total = blocks.reduce((sum, block) => ({ calls: sum.calls + block.calls, answered: sum.answered + block.answered, seconds: sum.seconds + block.callSeconds }), { calls: 0, answered: 0, seconds: 0 });
  const max = Math.max(1, ...blocks.map((block) => block.calls));
  const sentences = blocks.map((block) => blockSentence(t, locale, timeZone, block));
  const picked = chosen === null ? undefined : sentences[chosen];
  const detail = picked === undefined ? t("reports.blocks.hint") : t("reports.blocks.detail", { sentence: sentenceStart(locale, picked) });

  // La legenda dice che cosa è a righe in questa giornata: la pausa con le sue ore, chiuso, non ancora.
  const pauses = blocks.filter((block) => block.state === "pause");
  const firstPause = pauses[0];
  const lastPause = pauses.at(-1);
  const striped = [
    ...(firstPause && lastPause ? [t("reports.blocks.legendPause", { from: shortClock(timeZone, firstPause.start), to: shortClock(timeZone, lastPause.end) })] : []),
    ...(blocks.some((block) => block.state === "closed") ? [t("reports.blocks.legendClosed")] : []),
    ...(blocks.some((block) => block.state === "future") ? [t("reports.blocks.legendFuture")] : []),
  ];
  const legend = striped.length > 0 ? t("reports.blocks.legendStriped", { parts: new Intl.ListFormat(locale, { type: "disjunction" }).format(striped) }) : null;
  const columns = { "--g-report-blocks": blocks.length } as CSSProperties;

  return (
    <>
      <dl className="g-report-day-facts">
        {(["phone", "screen", "idle"] as const).map((state) => (
          <div key={state} data-state={state}>
            <dt>{t(`reports.blocks.facts.${state}`)}</dt>
            <dd>{factOf(state)}</dd>
          </div>
        ))}
        <div>
          <dt>{t("reports.blocks.facts.calls")}</dt>
          <dd>{t("reports.blocks.factCalls", { calls: formatCount(locale, total.calls), answered: formatCount(locale, total.answered), time: minutesOf(locale, total.seconds) })}</dd>
        </div>
      </dl>
      <div className="g-report-blocks-frame">
        <div className="g-report-blocks" style={columns} role="group" aria-label={t("reports.blocks.barsLabel")}>
          {blocks.map((block, index) => (
            <PlainButton
              key={block.start}
              className="g-report-block"
              data-state={block.state}
              aria-label={sentences[index]}
              onMouseEnter={() => onChoose(index)}
              onFocus={() => onChoose(index)}
              onClick={() => onChoose(index)}
            >
              <span className="g-report-block__count" aria-hidden="true">
                {block.calls > 0 ? formatCount(locale, block.calls) : null}
              </span>
              <span className="g-report-bar__stack" /* design-eccezione: variabile locale con un valore calcolato dai dati */ style={{ "--g-height": `${((block.calls / max) * 78).toFixed(1)}%` } as CSSProperties} aria-hidden="true">
                {block.calls > 0 ? (
                  <>
                    <span className="g-report-bar__no" /* design-eccezione: variabile locale con un valore calcolato dai dati */ style={{ "--g-share": block.calls - block.answered } as CSSProperties} />
                    <span className="g-report-bar__yes" /* design-eccezione: variabile locale con un valore calcolato dai dati */ style={{ "--g-share": block.answered } as CSSProperties} />
                  </>
                ) : null}
              </span>
              <span className="g-report-block__extra" aria-hidden="true">
                {block.activities > 0 ? t("reports.blocks.extra", { count: formatCount(locale, block.activities) }) : null}
              </span>
            </PlainButton>
          ))}
        </div>
        <div className="g-report-blocks-ticks" style={columns} aria-hidden="true">
          {blocks.map((block, index) =>
            index % TICK_EVERY === 0 ? (
              <span key={block.start} className="g-report-blocks__tick" /* design-eccezione: variabile locale con un valore calcolato dai dati */ style={{ "--g-start": index + 1, "--g-span": Math.min(TICK_EVERY, blocks.length - index) } as CSSProperties}>
                {formatNumericTime(timeZone, block.start)}
              </span>
            ) : null,
          )}
        </div>
      </div>
      <p className="g-report-detail" role="status">
        {detail}
      </p>
      <p className="g-report-legend">
        {(["phone", "screen", "idle"] as const).map((state) => (
          <span key={state}>
            <span className="g-report-legend__block" data-state={state} />
            {t(`reports.blocks.facts.${state}`)}
          </span>
        ))}
        {legend === null ? null : <span>{legend}</span>}
      </p>
    </>
  );
}
